import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Image } from 'rebass/styled-components';
import { Button, Message } from '@getro/rombo';
import { NavLink } from 'react-router-dom';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { Contact, Zap, MoreVertical, Pause, Play, RefreshCcw, Trash } from 'lucide-react';
import LinkedinLogo from '../../../assets/linkedin.svg';
import { IntegrationStatus } from './IntegrationStatus';
import DisconnectLinkedinSync from './DisconnectLinkedinSync';
import PauseLinkedinSync from './PauseLinkedinSync';

export const LinkedinIntegrationCard = ({
  linkedinSync: { syncStatus, lastSyncedAt, contactsSynced = 0 },
  getLinkedinSyncLink,
  onUpdateLinkedinSync,
  onRemoveConfirm,
  isRemoving,
  isPausing,
  variant = 'default',
}) => {
  const isOnboarding = variant === 'onboarding';
  const [showRemove, setShowRemove] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const isConnected = !!syncStatus && syncStatus !== 'not_connected';
  const showPause = ['syncing', 'in_sync', 'active'].includes(syncStatus);
  const showResume = syncStatus === 'paused';
  const showReconnect = syncStatus === 'reconnection_needed';
  const shouldShowContactsSynced = syncStatus !== 'syncing';

  const handleResume = async (e) => {
    e.stopPropagation();
    await onUpdateLinkedinSync({ status: 'resume' });
  };

  return (
    <Flex
      sx={{
        gap: '16px',
        p: '16px',
        border: '1px solid',
        borderColor: '#dee4ed',
        borderRadius: '4px',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '600px',
      }}
    >
      <Flex sx={{ gap: '16px' }} alignItems="flex-start" width="100%">
        {/* Icon column */}
        <Image width="32px" height="32px" src={LinkedinLogo} alt="LinkedIn" />
        {/* Content column */}
        <Flex flexDirection="column" sx={{ gap: '8px' }} flex={1}>
          {/* Header row: title and actions */}
          <Flex alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
            <Text color="text.dark" fontWeight="500" fontSize="14px">
              LinkedIn contacts sync
            </Text>
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              {isConnected ? (
                <IntegrationStatus status={syncStatus} />
              ) : (
                <Button as={NavLink} to={getLinkedinSyncLink('sync')} size="small" variant="primary">
                  Connect
                </Button>
              )}
              {!isOnboarding && isConnected && (
                <Box
                  sx={{
                    minWidth: '24px',
                    maxHeight: '24px',
                    height: '24px',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    alignItems: 'center',
                  }}
                  tx="dataTable"
                  data-testid="row-action"
                  display="flex"
                >
                  <Dropdown
                    placement="right-start"
                    trigger={
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        width="24px"
                        height="24px"
                        sx={{ cursor: 'pointer', svg: { color: 'text.main' } }}
                        aria-label="More actions"
                      >
                        <Box as={MoreVertical} size="16px" aria-hidden="true" />
                      </Flex>
                    }
                  >
                    <Card
                      sx={{ cursor: 'pointer', width: '228px', gap: '4px' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {showPause && (
                        <Flex
                          sx={{ gap: '8px', py: '4px', color: 'text.main' }}
                          alignItems="center"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowPauseModal(true);
                          }}
                        >
                          <Box as={Pause} size="16px" aria-hidden="true" />
                          <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                            Pause
                          </Text>
                        </Flex>
                      )}
                      {showResume && (
                        <Flex
                          sx={{ gap: '8px', py: '4px', color: 'text.main' }}
                          alignItems="center"
                          onClick={handleResume}
                        >
                          <Box as={Play} size="16px" aria-hidden="true" />
                          <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                            Resume
                          </Text>
                        </Flex>
                      )}
                      {showReconnect && (
                        <Flex
                          as={NavLink}
                          to={getLinkedinSyncLink('reconnect')}
                          sx={{ gap: '8px', py: '4px', color: 'text.main' }}
                          alignItems="center"
                        >
                          <Box as={RefreshCcw} size="16px" aria-hidden="true" />
                          <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                            Reconnect
                          </Text>
                        </Flex>
                      )}
                      <Flex
                        sx={{
                          gap: '8px',
                          py: '4px',
                          color: 'text.error',
                          '&:hover': { color: 'text.error' },
                        }}
                        alignItems="center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRemove(true);
                        }}
                      >
                        <Box as={Trash} size="16px" aria-hidden="true" />
                        <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                          Remove
                        </Text>
                      </Flex>
                    </Card>
                  </Dropdown>
                </Box>
              )}
            </Flex>
          </Flex>
          {/* Description */}
          <Text color="text.subtle" fontSize="14px" lineHeight="1.4">
            Sync your LinkedIn connections to Getro and decide which networks can access them.
          </Text>
          {/* Details */}
          {isConnected && (
            <Flex flexDirection="column" sx={{ gap: '8px', mt: '16px' }}>
              {shouldShowContactsSynced && (
                <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                  <Box as={Contact} width="16px" height="16px" aria-hidden="true" />
                  <Text color="text.main" fontSize="14px">
                    {contactsSynced} contacts synced
                  </Text>
                </Flex>
              )}
              <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                <Box as={Zap} width="16px" height="16px" aria-hidden="true" />
                {syncStatus === 'syncing' ? (
                  <Text color="text.main" fontSize="14px">
                    Sync in progress, this may take up to 3 hours.
                  </Text>
                ) : (
                  <Text color="text.main" fontSize="14px">
                    {`Last synced on ${
                      lastSyncedAt
                        ? new Date(lastSyncedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'
                    }`}
                  </Text>
                )}
              </Flex>
              {syncStatus === 'reconnection_needed' && (
                <Message
                  type="warning"
                  content={
                    <Text>
                      Your LinkedIn sync has stopped working.{' '}
                      <Box
                        as={NavLink}
                        to={getLinkedinSyncLink('reconnect')}
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        Reconnect now
                      </Box>{' '}
                      to keep your new connections synced in Getro.
                    </Text>
                  }
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {showRemove && (
        <DisconnectLinkedinSync
          isSubmitting={isRemoving}
          onCancel={() => setShowRemove(false)}
          onConfirm={async () => {
            await onRemoveConfirm?.();
            setShowRemove(false);
          }}
        />
      )}
      {showPauseModal && (
        <PauseLinkedinSync
          isSubmitting={isPausing}
          onCancel={() => setShowPauseModal(false)}
          onPauseLinkedinSync={async () => {
            await onUpdateLinkedinSync({ status: 'pause' });
            setShowPauseModal(false);
          }}
        />
      )}
    </Flex>
  );
};

LinkedinIntegrationCard.propTypes = {
  linkedinSync: PropTypes.shape({
    syncStatus: PropTypes.oneOf(['syncing', 'in_sync', 'paused', 'reconnection_needed', 'not_connected', 'active']),
    lastSyncedAt: PropTypes.string,
    contactsSynced: PropTypes.number,
  }),
  getLinkedinSyncLink: PropTypes.func.isRequired,
  onUpdateLinkedinSync: PropTypes.func,
  onRemoveConfirm: PropTypes.func,
  isRemoving: PropTypes.bool,
  isPausing: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'onboarding']),
};

LinkedinIntegrationCard.defaultProps = {
  linkedinSync: {
    syncStatus: 'not_connected',
    lastSyncedAt: null,
    contactsSynced: 0,
  },
  onUpdateLinkedinSync: undefined,
  onRemoveConfirm: undefined,
  isRemoving: false,
  isPausing: false,
  variant: 'default',
};
